/**
 * PausedRow — renders a family in « Mis en pause (offre) » or « Désactivés » groups.
 *
 * Both variants: « Réactiver » calls PATCH { status: 'active' }; the server picks the
 * members by the row's archivedReason (C11: a 'plan_limit' family is reactivated whole,
 * lot G step 15). A refusal (409 ALLOWANCE_EXCEEDED, 400 BRAND_ / AXIS_PROMPT_LIMIT,
 * 409 PROMPT_DELETED) greys the button and shows the UI-SPEC §2.4b line under the row.
 *
 * @see B-popup-frontend.md step 11a, UI-SPEC §2.4b
 */
import { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { BrandQueryAxis, AllowanceFigures, SaveWarnings } from '@traaker/shared';
import type { Family, LimitSlot } from './promptStudioModel';
import { limitReason, lastRunOf, structuralRowError, slotText } from './promptStudioModel';
import { LastRunCell } from './PromptRow';
import { QuotaWarningDialog } from './QuotaWarningDialog';
import { brandQueriesApi } from '../../services/brand-queries';
import type { PromptLastRun } from '@traaker/shared';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PausedRowProps {
  family: Family;
  /** 'plan' = Mis en pause (offre). 'user' = Désactivés. */
  variant: 'plan' | 'user';
  brandId: string;
  runs: Record<string, PromptLastRun> | null;
  /** Pre-computed greying reason from GET .../allowances (needRowsReason). null = not greyed. */
  preGreyed?: LimitSlot | null;
  /** Tenant tier — agency 'user' rows preview before saving (QuotaWarningDialog on warnings). */
  tier?: string;
  onChanged?: () => void;
}

/**
 * The « Réactiver » refusal line (UI-SPEC §2.4b; client strings, never the server's
 * message), or null when the error is not a refusal.
 */
export function reactivateRefusal(err: unknown, axis: BrandQueryAxis | 'pending', t: TFunction): string | null {
  const e = err as {
    status?: number;
    body?: { code?: string; axis?: BrandQueryAxis; figuresAfter?: AllowanceFigures };
  };
  const code = e?.body?.code;
  if (e?.status === 409 && code === 'ALLOWANCE_EXCEEDED' && e.body?.figuresAfter) {
    const slot = limitReason(e.body.figuresAfter, 'promptRequests');
    return t('promptStudio.paused.refused', { reason: slot ? slotText(t, slot) : '' });
  }
  if (e?.status === 409 && code === 'PROMPT_DELETED') return t('promptStudio.disabled.deletedRefused');
  if (e?.status === 400 && code) {
    const slot = structuralRowError(code, e.body?.axis ?? axis, 'reactivate');
    if (slot) return slotText(t, slot);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PausedRow({
  family,
  variant,
  brandId,
  runs,
  preGreyed,
  tier,
  onChanged,
}: PausedRowProps) {
  const { t } = useTranslation();
  const { root } = family;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [quotaWarnings, setQuotaWarnings] = useState<SaveWarnings[] | null>(null);

  const lastRun = lastRunOf(family, runs);
  const isAgency = tier === 'agency';

  const isDisabled = loading || !!preGreyed || !!refusal;

  async function doSave() {
    const axis = root.axis as BrandQueryAxis | 'pending';
    await brandQueriesApi.update(brandId, root.queryId, { axis, status: 'active' });
    onChanged?.();
  }

  async function handleReactivate() {
    if (isDisabled) return;
    setError(null);
    setLoading(true);
    const axis = root.axis as BrandQueryAxis | 'pending';
    try {
      // Agency 'user' rows: preview first (C17.3); 'plan' rows: save directly.
      if (isAgency && variant === 'user') {
        const res = await brandQueriesApi.update(brandId, root.queryId, { axis, status: 'active', preview: true });
        if (res.warnings && res.warnings.length > 0) {
          setQuotaWarnings(res.warnings);
          return;
        }
      }
      await doSave();
    } catch (err: unknown) {
      const line = reactivateRefusal(err, axis, t);
      if (line) setRefusal(line);
      else setError(t('promptStudio.common.saveFailed'));
    } finally {
      setLoading(false);
    }
  }

  async function handleWarningConfirm() {
    setQuotaWarnings(null);
    setLoading(true);
    const axis = root.axis as BrandQueryAxis | 'pending';
    try {
      await doSave();
    } catch (err: unknown) {
      const line = reactivateRefusal(err, axis, t);
      if (line) setRefusal(line);
      else setError(t('promptStudio.common.saveFailed'));
    } finally {
      setLoading(false);
    }
  }

  // Tooltip text for disabled button
  const disabledTitle = preGreyed ? slotText(t, preGreyed) : '';

  return (
    <>
    <PausedRowShell
      text={root.text}
      line={error ?? refusal}
      disabled={isDisabled}
      disabledTitle={disabledTitle}
      loading={loading}
      onReactivate={handleReactivate}
    >
      {/* Passage */}
      <LastRunCell run={lastRun} paused />
    </PausedRowShell>
    <QuotaWarningDialog
      open={quotaWarnings !== null && quotaWarnings.length > 0}
      warnings={quotaWarnings}
      onConfirm={handleWarningConfirm}
      onCancel={() => setQuotaWarnings(null)}
    />
  </>
  );
}

export interface PausedRowShellProps {
  text: string;
  /** The amber line under the text (a refusal, an error). */
  line?: string | null;
  disabled: boolean;
  disabledTitle?: string;
  loading: boolean;
  onReactivate: () => void;
  /** Cells between the text and « Réactiver » (the prompt row's Passage). */
  children?: React.ReactNode;
}

/** The §2.4b paused row frame, shared by the prompt and the conversation rows (lot I3). */
export function PausedRowShell({ text, line, disabled, disabledTitle = '', loading, onReactivate, children }: PausedRowShellProps) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 46,
        opacity: 0.55,
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,.06)',
        flexShrink: 0,
      }}
    >
      {/* Grey axis rail */}
      <Box sx={{ width: 6, minWidth: 6, alignSelf: 'stretch', bgcolor: '#d1d5db', flexShrink: 0 }} />

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0, px: 1.5 }}>
        <Box
          sx={{
            fontSize: 13,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
          }}
        >
          {text}
        </Box>
        {/* Inline error / refusal line */}
        {line && (
          <Box sx={{ fontSize: 11.5, color: 'warning.main', mt: 0.25 }}>
            {line}
          </Box>
        )}
      </Box>

      {children}

      {/* Réactiver button (opacity: 1 per spec) */}
      <Tooltip title={disabledTitle} arrow placement="top">
        <Box
          component="button"
          onClick={onReactivate}
          disabled={disabled}
          sx={{
            opacity: 1, // always full opacity per §2.4b
            fontSize: 12,
            color: disabled ? 'text.disabled' : '#7c3aed',
            border: '1px solid',
            borderColor: disabled ? 'rgba(0,0,0,.12)' : 'rgba(124,58,237,.3)',
            borderRadius: 1,
            bgcolor: 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer',
            px: 1,
            py: 0.25,
            mr: 1.5,
            flexShrink: 0,
            '&:hover:not(:disabled)': { bgcolor: 'rgba(124,58,237,.06)' },
          }}
        >
          {loading ? '…' : t('promptStudio.paused.reactivate')}
        </Box>
      </Tooltip>

      <Box sx={{ width: 18, minWidth: 18, flexShrink: 0 }} />
    </Box>
  );
}
